import ChannelUtils from '@shared/utils/channels';
import Content from '@shared/types/enums/content';
import DetailBackground from '@components/elements/detail/detailBackground';
import DetailContext from '@shared/components/utils/context/detailContext';
import DetailLeft from '@components/sections/detail/detailLeft';
import DetailRight from '@components/sections/detail/detailRight';
import DetailTop from '@components/sections/detail/detailTop';
import Event from '../../../shared/classes/event';
import Focus from '../../../shared/types/enums/focus';
import MediaProduction from '@shared/classes/mediaProduction';
import Plan from '@shared/classes/plan';
import Rail from '../../../shared/classes/rail';
import Routes from '../../../shared/types/enums/routes';
import goToContent from '@shared/actions/content/goToContent';
import useAssetInfo from '@shared/components/utils/hooks/useAssetInfo';
import useKeyboard from '../../../shared/components/utils/hooks/useKeyboard';
import { Coordinates } from '@shared/utils/floatingMenu';
import { openFloatingMenu } from '../../../shared/store/thunks/floatingMenu.thunk';
import { popRoute } from '../../../shared/store/thunks/route.thunk';
import { pushFocus, setFocus } from '../../../shared/store/slices/focus.slice';
import { store } from '../../../shared/store/store';
import { useAppDispatch } from '../../../shared/store/hooks';
import { useEffect, useRef } from 'react';

interface Props {
  asset: Event;
  buttons: {
    action: () => void;
    title: string;
  }[];
  complete: boolean;
  related: Rail | null;
  synopsis?: string[];
  upgradablePlan: Plan | null;
}

export default function TDetail(props: Props) {
  const { buttons, complete, synopsis, asset } = props;
  const assetInfo = useAssetInfo({ asset, extended: true });

  const dispatch = useAppDispatch();
  const dataRef = useRef(props);
  dataRef.current = props;

  // Effect hooks

  useEffect(() => {
    if (!complete) {
      return;
    }
    if (!buttons.length && synopsis?.length && synopsis?.length > 1) {
      dispatch(setFocus(Focus.DETAIL_SYNOPSIS));
    } else {
      dispatch(setFocus(Focus.DETAIL_BUTTONS));
    }
  }, [complete]);

  // Keyboard hooks

  useKeyboard({
    canBeFocused: false,
    keyDownHandler: (key) => {
      const { focus: focusSlice } = store.getState();
      const areButtonsFocused = focusSlice?.focus === Focus.DETAIL_BUTTONS;
      const areRelatedFocused =
        (focusSlice?.focus as number[])?.length &&
        (focusSlice?.focus as number[])?.includes(
          parseInt(`${Focus.RAILS_SUB_LIST}${0}`)
        );
      const isSynopsisFocused = focusSlice?.focus === Focus.DETAIL_SYNOPSIS;
      const hasButtons = !!dataRef.current?.buttons?.length;
      const hasRelated = !!dataRef.current?.related;
      const hasSynopsis =
        dataRef.current?.synopsis?.length &&
        dataRef.current?.synopsis.length > 1;

      switch (key) {
        case 'BACK':
          if (areButtonsFocused || isSynopsisFocused || areRelatedFocused) {
            dispatch(popRoute());
          }
          break;
        case 'LEFT':
          if (
            !hasButtons &&
            ((!hasSynopsis && !hasRelated) || areButtonsFocused)
          ) {
            dispatch(pushFocus(Focus.MENU));
          }
          break;
        case 'DOWN':
          // Buttons are focused
          if (areButtonsFocused && hasSynopsis) {
            // Focus synopsis
            dispatch(setFocus(Focus.DETAIL_SYNOPSIS));
            return;
          }
          // Buttons or synopsis are focused
          if ((areButtonsFocused || isSynopsisFocused) && hasRelated) {
            // Focus related
            dispatch(setFocus([parseInt(`${Focus.RAILS_SUB_LIST}${0}`)]));
          }
          break;
        case 'UP':
          // Synopsis is focused
          if (isSynopsisFocused) {
            // Focus buttons
            if (hasButtons) {
              dispatch(setFocus(Focus.DETAIL_BUTTONS));
            }
          }
          // Related are focused
          else if (areRelatedFocused) {
            // Focus synopsis
            if (hasSynopsis) {
              dispatch(setFocus(Focus.DETAIL_SYNOPSIS));
            }
            // Focus buttons
            else {
              dispatch(setFocus(Focus.DETAIL_BUTTONS));
            }
          }
          break;
      }
    },
    keyUpHandler: (key) => {
      switch (key) {
        case 'SELECT':
          const { focus: focusSlice } = store.getState();
          const { floatingMenu } = store.getState();

          const areRelatedFocused =
            (focusSlice?.focus as number[])?.length &&
            (focusSlice?.focus as number[])?.includes(
              parseInt(`${Focus.RAILS_SUB_LIST}${0}`)
            );
          const upgradablePlan = ChannelUtils.getUpgradablePlan(
            dataRef.current.asset?.channel
          );
          if (!dataRef.current.asset) return;
          if (areRelatedFocused) return;
          if (floatingMenu.open) return;
          if (upgradablePlan) return;
          if (
            dataRef.current.asset.type === Content.EPG_EVENT &&
            !dataRef.current.asset.isCurrentEvent()
          ) {
            return;
          }
          if (
            (dataRef.current.asset as MediaProduction).type ===
              Content.MEDIA_PRODUCTION &&
            !(dataRef.current.asset as MediaProduction).streams
          ) {
            return;
          }
          // Go to content if every condition is met
          goToContent(
            dataRef.current.asset,
            false,
            dataRef.current.asset.mocked
          );
          break;
      }
    },
    keyPressHandler: () => {
      const { floatingMenu } = store.getState();
      if (!floatingMenu.open) {
        dispatch(
          openFloatingMenu({
            route: Routes.DETAIL,
            asset: dataRef.current.asset,
            position: Coordinates.DETAIL,
          })
        );
      }
    },
  });

  return (
    <DetailContext.Provider value={{ ...props, assetInfo }}>
      <DetailBackground>
        <DetailTop />
        <DetailLeft />
        <DetailRight />
      </DetailBackground>
    </DetailContext.Provider>
  );
}
